import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { X, Settings, Crown, Shield, CreditCard, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { APP_VERSION } from '../config/version';

const SettingsModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate('/', { replace: true });
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-charcoal/60 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-beige shrink-0">
          <h2 className="text-xl font-medium text-charcoal flex items-center gap-2">
            <Settings size={20} className="text-sage" />
            Profile Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-soft hover:text-charcoal transition-colors"
            data-testid="close-settings-btn"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto min-h-0">
          <div className="space-y-6">
            {/* User Info */}
            <div className="flex items-center gap-4 pb-4 border-b border-beige">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-sage-light flex items-center justify-center">
                  <span className="text-sage-dark text-lg font-medium">
                    {user?.name?.charAt(0) || '?'}
                  </span>
                </div>
              )}
              <div>
                <p className="font-medium text-charcoal">{user?.name}</p>
                <p className="text-sm text-gray-soft">{user?.email}</p>
                {user?.handle && (
                  <p className="text-xs text-sage mt-0.5">@{user.handle}</p>
                )}
              </div>
            </div>

              {/* Subscription Info */}
              {(() => {
                const isUnlimited = user?.is_unlimited === true;
                const count = user?.stash_item_count || 0;
                const limit = user?.stash_item_limit || 20;
                const pct = limit > 0 ? (count / limit) * 100 : 0;
                const isAtLimit = !isUnlimited && count >= limit;
                const isNearLimit = !isUnlimited && pct >= 85 && !isAtLimit;

                if (isUnlimited) {
                  return (
                    <div className="pt-4 border-t border-beige">
                      <h3 className="font-medium text-charcoal mb-2">Subscription</h3>
                      <div className="p-4 rounded-lg bg-amber-50 border border-amber-300">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Crown className="h-5 w-5 text-amber-500 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-amber-700">Unlimited Plan</p>
                              <p className="text-xs text-amber-600 mt-0.5">
                                {count} items — no limits
                              </p>
                            </div>
                          </div>
                          <span className="px-3 py-1 rounded-full bg-amber-400 text-white text-xs font-medium" data-testid="settings-unlimited-badge">
                            Unlimited
                          </span>
                        </div>
                        {/* Billing link within subscription */}
                        <button
                          onClick={() => {
                            onClose();
                            navigate('/billing');
                          }}
                          className="mt-3 w-full flex items-center justify-between p-3 bg-white/60 hover:bg-white rounded-lg transition-colors"
                          data-testid="settings-billing-link"
                        >
                          <div className="flex items-center gap-2">
                            <CreditCard size={16} className="text-amber-600" />
                            <span className="text-sm text-amber-700">Manage Billing</span>
                          </div>
                          <span className="text-amber-500">→</span>
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="pt-4 border-t border-beige">
                    <h3 className="font-medium text-charcoal mb-2">Subscription</h3>
                    <div className={`p-4 rounded-lg ${
                      isAtLimit
                        ? 'bg-red-50 border border-red-200'
                        : isNearLimit
                          ? 'bg-amber-50 border border-amber-200'
                          : 'bg-cream'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium capitalize ${
                            isAtLimit ? 'text-red-700' : isNearLimit ? 'text-amber-700' : 'text-charcoal'
                          }`}>
                            {user?.subscription_tier || 'Free'} Plan
                          </p>
                          <p className={`text-xs mt-0.5 ${
                            isAtLimit ? 'text-red-600' : isNearLimit ? 'text-amber-600' : 'text-gray-soft'
                          }`}>
                            {count} / {limit} items used
                            {isAtLimit && ' — Limit reached!'}
                            {isNearLimit && ` — ${Math.round(pct)}% full`}
                          </p>
                          {/* Mini progress bar */}
                          <div className="w-32 h-1.5 bg-white/60 rounded-full mt-2 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-sage'
                              }`}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                        </div>
                        <button 
                          onClick={() => {
                            onClose();
                            navigate('/billing#pricing-calculator');
                          }}
                          className={`px-4 py-2 text-sm text-white rounded-lg transition-colors ${
                            isAtLimit
                              ? 'bg-red-500 hover:bg-red-600'
                              : isNearLimit
                                ? 'bg-amber-500 hover:bg-amber-600'
                                : 'bg-sage hover:bg-sage-dark'
                          }`}
                          data-testid="settings-upgrade-btn"
                        >
                          Upgrade
                        </button>
                      </div>
                      {/* Billing link within subscription */}
                      <button
                        onClick={() => {
                          onClose();
                          navigate('/billing');
                        }}
                        className={`mt-3 w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                          isAtLimit
                            ? 'bg-white/60 hover:bg-white'
                            : isNearLimit
                              ? 'bg-white/60 hover:bg-white'
                              : 'bg-white/60 hover:bg-white'
                        }`}
                        data-testid="settings-billing-link"
                      >
                        <div className="flex items-center gap-2">
                          <CreditCard size={16} className={
                            isAtLimit ? 'text-red-500' : isNearLimit ? 'text-amber-500' : 'text-sage'
                          } />
                          <span className={`text-sm ${
                            isAtLimit ? 'text-red-700' : isNearLimit ? 'text-amber-700' : 'text-charcoal'
                          }`}>Manage Billing</span>
                        </div>
                        <span className={
                          isAtLimit ? 'text-red-400' : isNearLimit ? 'text-amber-400' : 'text-gray-soft'
                        }>→</span>
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Sign Out Section */}
              <div className="pt-4 border-t border-beige">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 p-4 text-gray-soft hover:text-error hover:bg-red-50 rounded-lg transition-colors"
                  data-testid="settings-signout-btn"
                >
                  <LogOut size={18} />
                  <span className="font-medium text-sm">Sign Out</span>
                </button>
              </div>
            </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-beige flex items-center justify-between shrink-0">
          <span className="text-xs text-gray-light">Kura Stash {APP_VERSION}</span>
          <div className="flex items-center gap-3">
            <Link
              to="/privacy"
              onClick={onClose}
              className="flex items-center gap-1 text-xs text-gray-soft hover:text-sage transition-colors"
              data-testid="settings-privacy-link"
            >
              <Shield size={12} />
              Privacy
            </Link>
            <Link
              to="/terms"
              onClick={onClose}
              className="text-xs text-gray-soft hover:text-sage transition-colors"
              data-testid="settings-terms-link"
            >
              Terms
            </Link>
            <Link
              to="/cookies"
              onClick={onClose}
              className="text-xs text-gray-soft hover:text-sage transition-colors"
              data-testid="settings-cookies-link"
            >
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
